import * as React from 'react';
import { LocalDate, TemporalAdjusters } from 'js-joda';
import _ from 'lodash';
import clsx from 'clsx';

import { DateAvailabilityObject } from 'types';
import { findFocusDate, range } from 'utils/calendar';
import ConsumerCalendarMonth from 'components/calendar/ConsumerCalendarMonth';

interface Props {
  availabilityOverride?: boolean;
  availableDates?: DateAvailabilityObject;
  endDate?: LocalDate;
  ignoreYear?: boolean;
  initializeToFirstOffered?: boolean;
  lockedDates?: DateAvailabilityObject;
  modifierClassName?: string;
  offeredDates?: DateAvailabilityObject;
  onSelect(date: string): void;
  openDates?: DateAvailabilityObject;
  selectedDate?: string;
  soldDates?: DateAvailabilityObject;
  startDate?: LocalDate;
  /** By default, anywhere ConsumerCalendar is rendered, a default date (probably
   *  the same as selectedDate, or the current date) will be focused within the calendar.
   *  To turn off this behavior, set suppressAutoFocus to true. */
  suppressAutoFocus?: boolean;
  wideView?: boolean;
}

export type ConsumerCalendarProps = Props;

export const ConsumerCalendar: React.FunctionComponent<Props> = ({
  availabilityOverride,
  availableDates = {},
  endDate,
  ignoreYear,
  initializeToFirstOffered,
  lockedDates = {},
  modifierClassName,
  offeredDates = {},
  onSelect,
  openDates = {},
  selectedDate,
  soldDates = {},
  suppressAutoFocus = false,
  startDate,
  wideView = true,
}) => {
  let defaultSelectedDate = LocalDate.now();
  const { min } = range(offeredDates);
  if (selectedDate) {
    defaultSelectedDate = LocalDate.parse(selectedDate);
  } else if (min && initializeToFirstOffered) {
    defaultSelectedDate = LocalDate.parse(min);
  }

  const [displayedMonth, setDisplayedMonth] = React.useState<LocalDate>(
    defaultSelectedDate.with(TemporalAdjusters.firstDayOfMonth())
  );
  const [focusDate, setFocusDate] = React.useState<LocalDate | undefined>(
    suppressAutoFocus ? undefined : defaultSelectedDate
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, date: string) => {
    const newDate = findFocusDate(event, date);
    if (newDate) {
      setFocusDate(newDate);
      // if the date is the same, then it was selected
      if (newDate.equals(LocalDate.parse(date))) {
        handleSelect(date);
      }
    }
  };

  const handleSelect = (date: string) => {
    onSelect(date);
  };

  const handleClickPrev = () => {
    setDisplayedMonth(displayedMonth.minusMonths(1));
  };

  const handleClickNext = () => {
    setDisplayedMonth(displayedMonth.plusMonths(1));
  };

  let maxLocalDate;
  let minLocalDate;
  let nextDisabled;
  let prevDisabled;

  if (offeredDates) {
    const { min, max } = range(offeredDates);
    minLocalDate = min ? LocalDate.parse(min) : LocalDate.now();
    maxLocalDate = max ? LocalDate.parse(max) : LocalDate.now();
    nextDisabled = !max || displayedMonth.plusMonths(1).isAfter(maxLocalDate);
    prevDisabled = !min || displayedMonth.minusMonths(1).isBefore(minLocalDate.minusMonths(1));
  }

  if (_.isEmpty(offeredDates)) {
    minLocalDate = startDate;
    maxLocalDate = endDate;
    if (maxLocalDate && minLocalDate) {
      nextDisabled = displayedMonth.plusMonths(1).isAfter(maxLocalDate);
      prevDisabled = displayedMonth.minusMonths(1).isBefore(minLocalDate.minusMonths(1));
    }
  }

  const className = clsx('ConsumerCalendar', modifierClassName, {
    'ConsumerCalendar--double': wideView,
    'ConsumerCalendar--single': !wideView,
  });

  const passedProps = {
    availabilityOverride,
    availableDates,
    endDate,
    ignoreYear,
    initializeToFirstOffered,
    lockedDates,
    modifierClassName,
    offeredDates,
    onSelect,
    openDates,
    selectedDate,
    soldDates,
    startDate,
    wideView,
  };

  return (
    <div className={className} data-testid="consumer-calendar">
      <ConsumerCalendarMonth
        {...passedProps}
        disableNext={nextDisabled}
        disablePrev={prevDisabled}
        focusDate={focusDate}
        handleKeyDown={handleKeyDown}
        month={displayedMonth}
        selectedDates={selectedDate ? [selectedDate] : []}
        onClickNext={handleClickNext}
        onClickPrev={handleClickPrev}
        onSelectDate={handleSelect}
        data-testid="calendar-first"
      />

      <ConsumerCalendarMonth
        {...passedProps}
        disableNext={nextDisabled}
        disablePrev={prevDisabled}
        focusDate={focusDate}
        handleKeyDown={handleKeyDown}
        month={displayedMonth.plusMonths(1)}
        selectedDates={selectedDate ? [selectedDate] : []}
        onClickNext={handleClickNext}
        onClickPrev={handleClickPrev}
        onSelectDate={handleSelect}
        data-testid="calendar-next"
      />
    </div>
  );
};

export default ConsumerCalendar;
